/* ==========================================================================
   PLAYNOSTALGIA — pn-theme.js
   JavaScript global, vanilla, sans dépendance externe.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- Utilitaires partagés ---------- */
  var PN = (window.PN = window.PN || {});

  PN.trapFocus = function (container, firstFocus) {
    var selector =
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(container.querySelectorAll(selector), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    container._pnReleaseFocus = function () {
      container.removeEventListener('keydown', onKeydown);
    };

    window.setTimeout(function () {
      var target = firstFocus || container.querySelector(selector);
      if (target) target.focus();
    }, 60);
  };

  PN.releaseFocus = function (container) {
    if (container && typeof container._pnReleaseFocus === 'function') {
      container._pnReleaseFocus();
      container._pnReleaseFocus = null;
    }
  };

  PN.lockScroll = function (lock) {
    document.documentElement.classList.toggle('pn-no-scroll', !!lock);
    document.body.classList.toggle('pn-no-scroll', !!lock);
  };

  PN.toast = function (message) {
    var el = document.getElementById('pn-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pn-toast';
      el.className = 'pn-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('pn-is-visible');
    window.clearTimeout(el._pnTimer);
    el._pnTimer = window.setTimeout(function () {
      el.classList.remove('pn-is-visible');
    }, 2600);
  };

  PN.money = function (cents) {
    var format = (window.PNSettings && window.PNSettings.moneyFormat) || '{{amount}}';
    var value = (cents / 100).toFixed(2);
    if (format.indexOf('amount_no_decimals') > -1) {
      value = Math.round(cents / 100).toString();
    } else if (format.indexOf('amount_with_comma_separator') > -1) {
      value = value.replace('.', ',');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ---------- Header sticky ---------- */
  function initHeader() {
    var header = document.querySelector('[data-pn-header]');
    if (!header) return;
    var ticking = false;

    function update() {
      header.classList.toggle('pn-header--scrolled', window.scrollY > 12);
      ticking = false;
    }

    update();
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ---------- Panneaux génériques (menu mobile, recherche) ---------- */
  function initPanels() {
    var openers = document.querySelectorAll('[data-pn-open]');
    var closers = document.querySelectorAll('[data-pn-close]');

    function open(id) {
      var panel = document.getElementById(id);
      if (!panel) return;
      panel.setAttribute('aria-hidden', 'false');
      PN.lockScroll(true);
      PN.trapFocus(panel);
      panel._pnOpener = document.activeElement;
    }

    function close(panel) {
      if (!panel) return;
      panel.setAttribute('aria-hidden', 'true');
      PN.releaseFocus(panel);
      if (!document.querySelector('[data-pn-panel][aria-hidden="false"]')) {
        PN.lockScroll(false);
      }
      if (panel._pnOpener && typeof panel._pnOpener.focus === 'function') {
        panel._pnOpener.focus();
      }
    }

    PN.openPanel = open;
    PN.closePanel = close;

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open(btn.getAttribute('data-pn-open'));
      });
    });

    Array.prototype.forEach.call(closers, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        close(btn.closest('[data-pn-panel]'));
      });
    });

    document.addEventListener('click', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-pn-overlay')) {
        close(e.target.closest('[data-pn-panel]'));
      }
    });

    /* Ferme le menu mobile quand on clique un lien d'ancre (pas de rechargement) */
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-pn-panel] a[href]') : null;
      if (link) close(link.closest('[data-pn-panel]'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openPanel = document.querySelector('[data-pn-panel][aria-hidden="false"]');
      if (openPanel) close(openPanel);
    });
  }

  /* ---------- Animations au scroll ---------- */
  function initReveal() {
    var items = document.querySelectorAll('.pn-reveal');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) {
        el.classList.add('pn-is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pn-is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    Array.prototype.forEach.call(items, function (el, i) {
      if (!el.style.getPropertyValue('--pn-delay')) {
        el.style.setProperty('--pn-delay', (i % 4) * 70 + 'ms');
      }
      observer.observe(el);
    });
  }

  /* ---------- Vidéo : lecture à la demande ---------- */
  function initVideos() {
    var wrappers = document.querySelectorAll('[data-pn-video]');
    Array.prototype.forEach.call(wrappers, function (wrapper) {
      var btn = wrapper.querySelector('[data-pn-video-play]');
      var video = wrapper.querySelector('video');
      var iframeSrc = wrapper.getAttribute('data-pn-video-embed');

      if (!btn) return;

      btn.addEventListener('click', function () {
        if (video) {
          video.play();
          video.setAttribute('controls', 'controls');
        } else if (iframeSrc) {
          var iframe = document.createElement('iframe');
          iframe.setAttribute('src', iframeSrc);
          iframe.setAttribute('title', wrapper.getAttribute('data-pn-video-title') || 'Vidéo');
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
          iframe.setAttribute('allowfullscreen', 'allowfullscreen');
          iframe.setAttribute('loading', 'lazy');
          wrapper.querySelector('.pn-video__frame').appendChild(iframe);
        }
        btn.parentNode.hidden = true;
      });

      /* Lecture automatique uniquement si le navigateur l'autorise (muted). */
      if (video && video.hasAttribute('data-pn-autoplay')) {
        var attempt = video.play();
        if (attempt && typeof attempt.then === 'function') {
          attempt
            .then(function () {
              if (btn.parentNode) btn.parentNode.hidden = true;
            })
            .catch(function () {
              /* Autoplay refusé : le bouton de lecture reste visible. */
            });
        }
      }
    });
  }

  /* ---------- Newsletter : message de succès ---------- */
  function initNewsletter() {
    if (window.location.search.indexOf('customer_posted=true') === -1) return;
    var msg = document.querySelector('[data-pn-newsletter-success]');
    if (msg) msg.hidden = false;
  }

  function init() {
    initHeader();
    initPanels();
    initReveal();
    initVideos();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
